import React, { useCallback, useRef, useState, useContext, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Webcam from "react-webcam";

import { AuthContext } from "../../shared/context/auth-context";
import { useHttpClient } from "../../shared/hooks/http-hook";
import ErrorModal from "../../shared/components/UIElements/ErrorModal";
import LoadingSpinner from "../../shared/components/UIElements/LoadingSpinner";
import Button from "../../shared/components/FormElements/Button/Button";
import './WebcamPage.css';

const videoConstraints = {
    width: { min: 720 },
    height: { min: 480 },
    aspectRatio: 1.5,
    facingMode: "user"
};

const WebcamPage = () => {
  const navigate = useNavigate();
  const questionId = useParams().questionId;
  const question = useParams().question;
  const auth = useContext(AuthContext);
  const { isLoading, error, sendRequest, clearError } = useHttpClient();
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [stoppedCapturing, setStoppedCapturing] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [loadingCam, setLoadingCam] = useState(true);

  const handleUserMedia = () => {
    setTimeout(() => {
    // timer is optional if the loading is taking some time.
      setLoadingCam(false);
    }, 1000);
  };

  const handleDataAvailable = useCallback(
    ({ data }) => {
      if (data.size > 0) {
        setRecordedChunks((prev) => prev.concat(data));
      }
    },
    [setRecordedChunks]
  );

  const handleStartCaptureClick = useCallback(() => {
    setRecordedChunks([]);
    setCapturing(true);
    mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
      mimeType: "video/webm",
    });
    mediaRecorderRef.current.addEventListener(
      "dataavailable",
      handleDataAvailable
    );
    mediaRecorderRef.current.start();
  }, [webcamRef, setCapturing, mediaRecorderRef, handleDataAvailable]);

  const handleStopCaptureClick = useCallback(() => {
    mediaRecorderRef.current.stop();
    setCapturing(false);
    setStoppedCapturing(true);
  }, [mediaRecorderRef, setCapturing]);

  
  const handleVideoStop = useCallback(async () => {
    if (recordedChunks.length > 0) {
      const blob = new Blob(recordedChunks, {
        type: "video/webm",
      });
      const file = new File([blob], "react-recording.webm", { type: "video/webm" });
      const formData = new FormData();
      formData.append("title", "video uploaded");
      formData.append("video", file);
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL_PLAN}/upload/${questionId}`,
          "POST",
          formData,
          {
            Authorization: `Bearer ${auth.token}`,
          }
        );
        setRecordedChunks([]);
        navigate(`/question/answer/${questionId}/${question}`, {state:{id:responseData.filepath, filepath: responseData.filepath}});
      } catch (err) {
        // Handle the error
      }
    }
  }, [recordedChunks, sendRequest, auth.token, questionId, navigate, question]);

  useEffect(() => {
    if (stoppedCapturing) {
      console.log("triggered");
      handleVideoStop();
    }
  }, [recordedChunks, stoppedCapturing, handleVideoStop]);

    return (
        <React.Fragment>
          <ErrorModal error={error} onClear={clearError} />
          { isLoading && <LoadingSpinner asOverlay /> }
          {!isLoading && 
          <div className="webcam-page">
          <div className="question-webcam"><h1>{question}?</h1></div>
          <div className="webcam-container" >
          <div className="webcam">
          <Webcam audio={true} muted={true} mirrored={true} ref={webcamRef} videoConstraints={videoConstraints} onUserMedia={handleUserMedia} className="webcam-webcam"/>
          </div>
          </div>
          {capturing ? (
            <div className="webcam-button"><Button onClick={handleStopCaptureClick}>STOP</Button></div>
            ) : (
            <div className="webcam-button"><Button onClick={handleStartCaptureClick} disabled={loadingCam}>START</Button></div>
            )}
          </div>
          }
          </React.Fragment>
      );
};

export default WebcamPage;